import { useState } from "react";
import { Star } from "lucide-react";
import { setRfpPrimeContactHighValue, type RfpPrimeContact } from "../api/CrmApi";
import Modal from "shared/Modal";
import Button from "shared/Button";
import Alert from "shared/Alert";
import { FORM_CONTROL } from "shared/tableStyles";

interface Props {
  contact: RfpPrimeContact;
  onClose: () => void;
  onSaved: (updated: RfpPrimeContact) => void;
}

export default function FlagPrimeContactModal({ contact, onClose, onSaved }: Props) {
  const [notes, setNotes] = useState(contact.is_high_value ? contact.notes ?? "" : "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    setSaving(true);
    setError(null);
    try {
      const updated = await setRfpPrimeContactHighValue(contact.id, true, notes.trim() || undefined);
      onSaved(updated);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || "Failed to flag contact. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      size="md"
      title="Flag as High Value"
      description={contact.org_name}
      icon={<Star />}
      onClick={saving ? undefined : onClose}
      closeButtonCLick={saving ? undefined : onClose}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={confirm} disabled={saving}>
            {saving ? "Saving…" : "Flag Contact"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1.5">Why (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="e.g. Engaged reply, strong fit — give extra attention next time they appear on an RFP."
            className={`w-full px-3 py-2 ${FORM_CONTROL} resize-none`}
          />
        </div>
        <p className="text-[11px] text-muted-foreground">
          If this same email address appears as a prime candidate on a future RFP, it will be flagged automatically and surfaced with this reason.
        </p>
      </div>

      {error && (
        <Alert tone="danger" className="mt-4">{error}</Alert>
      )}
    </Modal>
  );
}
